// pages/ChatSpace/ChatSpaceInterface.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    LinearProgress,
    Chip,
    Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import ChatSpaceMessageBubble from './ChatSpaceMessageBubble';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';
import { v4 as uuidv4 } from 'uuid';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
    success: '#059669',
    danger: '#dc2626',
};

export default function ChatSpaceInterface({ sessionCode, onSessionChange, onCitationsChange, privacy }) {
    const { activeChatbot } = useChatbots();
    const [settings, setSettings] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [input, setInput] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [analyticsMode, setAnalyticsMode] = useState(false);
    const [imageGenerationMode, setImageGenerationMode] = useState(false);
    const [attachedImage, setAttachedImage] = useState(null);
    const [attachedImagePreview, setAttachedImagePreview] = useState(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const imageInputRef = useRef(null);

    const [internalSessionCode, setInternalSessionCode] = useState(null);
    const currentSessionCode = sessionCode !== undefined ? sessionCode : internalSessionCode;

    const isEmptyChat = messages.length === 0;

    // Add Menu State
    const [addMenuAnchor, setAddMenuAnchor] = useState(null);
    const addMenuOpen = Boolean(addMenuAnchor);

    // Dialog States
    const [websiteLinkDialogOpen, setWebsiteLinkDialogOpen] = useState(false);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [audioDialogOpen, setAudioDialogOpen] = useState(false);

    // Website Link State
    const [websiteLink, setWebsiteLink] = useState('');
    const [isAddingWebsite, setIsAddingWebsite] = useState(false);

    // Document Upload State
    const [selectedDocuments, setSelectedDocuments] = useState(null);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);

    // Image Upload State
    const [selectedImages, setSelectedImages] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    // Audio Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);
    const [audioStream, setAudioStream] = useState(null);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const audioChunksRef = useRef([]);
    const recordingTimeoutRef = useRef(null);
    const recordingIntervalRef = useRef(null);

    useEffect(() => {
        loadInitialSettings();
    }, []);

    useEffect(() => {
        if (settings) loadConversation(currentSessionCode);
    }, [currentSessionCode, settings]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadInitialSettings = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/api/chatspace/initial-data');
            const { settings: s } = response.data.data;
            setSettings(s);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Could not load chat interface.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadConversation = async (sc) => {
        if (!settings) return;
        setIsLoading(true);
        onCitationsChange?.([]);

        if (!sc) {
            setMessages([]);
            setIsLoading(false);
            return;
        }

        try {
            const url = `/api/chatspace/initial-data?session_code=${encodeURIComponent(sc)}`;
            const response = await axiosInstance.get(url);
            const { history } = response.data.data;
            setMessages(history?.length > 0 ? history : []);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Could not load conversation.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMessage = {
            id: `user-${Date.now()}`,
            message: input,
            is_from_user: true,
            attached_image: attachedImagePreview || null,
        };
        setMessages((prev) => [...prev, userMessage]);
        const currentInput = input;
        const currentAttachedImage = attachedImage;
        setInput('');
        setAttachedImage(null);
        setAttachedImagePreview(null);
        if (imageInputRef.current) imageInputRef.current.value = '';

        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        setIsSending(true);
        try {
            let response;

            if (imageGenerationMode) {
                const formData = new FormData();
                formData.append('message', currentInput);
                formData.append('privacy', privacy);
                formData.append('image_generation_mode', 'true');
                if (currentSessionCode) formData.append('session_code', currentSessionCode);
                if (currentAttachedImage) formData.append('image', currentAttachedImage);

                response = await axiosInstance.post('/api/chatspace/send-message', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                const payload = { message: currentInput, privacy, analytics_mode: analyticsMode };
                if (currentSessionCode) payload.session_code = currentSessionCode;

                response = await axiosInstance.post('/api/chatspace/send-message', payload);
            }

            const botMessage = response.data.data;
            setMessages((prev) => [...prev, botMessage]);

            if (!currentSessionCode && botMessage.session_code) {
                if (onSessionChange) {
                    onSessionChange(botMessage.session_code);
                } else {
                    setInternalSessionCode(botMessage.session_code);
                }
            }
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to get a response.', 'error');
            setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
        } finally {
            setIsSending(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!', 'success');
    };

    const handleCitationsClick = (msg) => {
        if (msg.citations?.length > 0) {
            onCitationsChange?.(msg.citations);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    // Add Menu Handlers
    const handleAddMenuClick = (event) => {
        setAddMenuAnchor(event.currentTarget);
    };

    const handleAddMenuClose = () => {
        setAddMenuAnchor(null);
    };

    // Website Link Handlers
    const handleOpenWebsiteLinkDialog = () => {
        handleAddMenuClose();
        setWebsiteLink('');
        setWebsiteLinkDialogOpen(true);
    };

    const handleCloseWebsiteLinkDialog = () => {
        setWebsiteLinkDialogOpen(false);
        setWebsiteLink('');
    };

    const handleAddWebsiteLink = async () => {
        if (!websiteLink.trim()) {
            showSnackbar('Please enter a website link.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsAddingWebsite(true);
        try {
            const params = { chatbot_id: activeChatbot.id };
            let linkToAdd = websiteLink.trim();
            if (!/^(https?:\/\/|mailto:|tel:)/i.test(linkToAdd)) {
                linkToAdd = 'https://' + linkToAdd;
            }
            const response = await axiosInstance.post(
                '/add-website-link',
                { website_link: linkToAdd },
                { params }
            );
            showSnackbar(response.data.message || 'Website link added and training started!', 'success');
            handleCloseWebsiteLinkDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to add website link.', 'error');
        } finally {
            setIsAddingWebsite(false);
        }
    };

    // Document Upload Handlers
    const handleOpenDocumentDialog = () => {
        handleAddMenuClose();
        setSelectedDocuments(null);
        setDocumentDialogOpen(true);
    };

    const handleCloseDocumentDialog = () => {
        setDocumentDialogOpen(false);
        setSelectedDocuments(null);
    };

    const handleDocumentFileChange = (event) => {
        setSelectedDocuments(event.target.files);
    };

    const handleUploadDocument = async () => {
        if (!selectedDocuments || selectedDocuments.length === 0) {
            showSnackbar('Please select document(s) to upload.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsUploadingDocument(true);
        try {
            const formData = new FormData();
            for (let i = 0; i < selectedDocuments.length; i++) {
                formData.append('files', selectedDocuments[i]);
            }
            formData.append('category_ids', JSON.stringify([]));

            const params = { chatbot_id: activeChatbot.id };
            const response = await axiosInstance.post('/upload-private-data', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params,
            });
            showSnackbar(response.data.message || 'Document(s) uploaded and training started!', 'success');
            handleCloseDocumentDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload document(s).', 'error');
        } finally {
            setIsUploadingDocument(false);
        }
    };

    // Image Upload Handlers
    const handleOpenImageDialog = () => {
        handleAddMenuClose();
        setSelectedImages(null);
        setImageDialogOpen(true);
    };

    const handleCloseImageDialog = () => {
        setImageDialogOpen(false);
        setSelectedImages(null);
    };

    const handleImageFileChange = (event) => {
        setSelectedImages(event.target.files);
    };

    const handleUploadImage = async () => {
        if (!selectedImages || selectedImages.length === 0) {
            showSnackbar('Please select image(s) to upload.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsUploadingImage(true);
        try {
            const formData = new FormData();
            for (let i = 0; i < selectedImages.length; i++) {
                formData.append('files', selectedImages[i]);
            }

            const params = { chatbot_id: activeChatbot.id };
            const response = await axiosInstance.post('/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params,
            });
            showSnackbar(response.data.message || 'Image(s) uploaded and training started!', 'success');
            handleCloseImageDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload image(s).', 'error');
        } finally {
            setIsUploadingImage(false);
        }
    };

    // Audio Recording Handlers
    const handleOpenAudioDialog = () => {
        handleAddMenuClose();
        setRecordingTime(0);
        setAudioDialogOpen(true);
    };

    const handleCloseAudioDialog = () => {
        if (isRecording) {
            stopRecording();
        }
        setAudioDialogOpen(false);
        setRecordingTime(0);
    };

    const startRecording = async () => {
        try {
            setIsRecording(true);
            setRecordingTime(0);
            audioChunksRef.current = [];

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { channelCount: 1 },
                video: false,
            });

            const recorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
            });

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                stream.getTracks().forEach((track) => track.stop());
                setIsRecording(false);
                setAudioStream(null);

                if (recordingTimeoutRef.current) {
                    clearTimeout(recordingTimeoutRef.current);
                    recordingTimeoutRef.current = null;
                }
                if (recordingIntervalRef.current) {
                    clearInterval(recordingIntervalRef.current);
                    recordingIntervalRef.current = null;
                }

                if (audioChunksRef.current.length > 0) {
                    await handleUploadAudio(audioChunksRef.current);
                }
            };

            setMediaRecorder(recorder);
            setAudioStream(stream);
            recorder.start(100);

            recordingTimeoutRef.current = setTimeout(() => {
                if (recorder.state === 'recording') {
                    recorder.stop();
                }
            }, 120000);

            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } catch (err) {
            console.error('Error starting recording:', err);
            showSnackbar(`Failed to start recording: ${err.message}`, 'error');
            setIsRecording(false);
        }
    };

    const stopRecording = () => {
        if (recordingTimeoutRef.current) {
            clearTimeout(recordingTimeoutRef.current);
            recordingTimeoutRef.current = null;
        }
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
        if (audioStream) {
            audioStream.getTracks().forEach((track) => track.stop());
            setAudioStream(null);
        }
    };

    const handleUploadAudio = async (chunks) => {
        if (!chunks || chunks.length === 0) {
            showSnackbar('No audio recorded.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsUploadingAudio(true);
        try {
            const audioBlob = new Blob(chunks, { type: 'audio/webm' });
            const formData = new FormData();
            formData.append('file', audioBlob, `recording_${uuidv4()}.webm`);
            formData.append('lang_type', 'en-US');

            const params = { chatbot_id: activeChatbot.id };
            const response = await axiosInstance.post('/upload-voice', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params,
            });
            showSnackbar(response.data.message || 'Audio uploaded and training started!', 'success');
            handleCloseAudioDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload audio.', 'error');
        } finally {
            setIsUploadingAudio(false);
        }
    };

    // Cleanup recording on unmount
    useEffect(() => {
        return () => {
            if (recordingTimeoutRef.current) {
                clearTimeout(recordingTimeoutRef.current);
            }
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            if (audioStream) {
                audioStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [audioStream]);

    // Quick action chips configuration
    const quickActions = [
        { id: 'analytics', label: 'Analytics', icon: <PsychologyIcon sx={{ fontSize: 16 }} /> },
        { id: 'image-generation', label: 'Image Generation', icon: <AutoFixHighIcon sx={{ fontSize: 16 }} /> },
    ];

    const handleQuickAction = (actionId) => {
        if (actionId === 'analytics') {
            setAnalyticsMode(true);
            setImageGenerationMode(false);
            setAttachedImage(null);
            setAttachedImagePreview(null);
            textareaRef.current?.focus();
        } else if (actionId === 'image-generation') {
            setImageGenerationMode(true);
            setAnalyticsMode(false);
            textareaRef.current?.focus();
        }
    };

    const handleImageAttachment = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setAttachedImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setAttachedImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeAttachedImage = () => {
        setAttachedImage(null);
        setAttachedImagePreview(null);
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };

    if (isLoading && !settings) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
            }}>
                <CircularProgress size={32} sx={{ color: colors.accent }} />
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textTertiary }}>
                    Loading chat...
                </Typography>
            </Box>
        );
    }

    if (!settings) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography sx={{ fontFamily, color: colors.danger }}>Chat unavailable.</Typography>
            </Box>
        );
    }

    const inputArea = (
        <Box sx={{ width: '100%' }}>
            <form onSubmit={handleSendMessage} style={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        bgcolor: 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(8px)',
                        borderRadius: '14px',
                        border: '1px solid rgba(0,0,0,0.08)',
                        p: 0.75,
                        '&:focus-within': {
                            borderColor: colors.accent,
                            boxShadow: `0 0 0 3px rgba(59,130,246,0.1)`,
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {/* Add Button */}
                    <IconButton
                        onClick={handleAddMenuClick}
                        disabled={isSending}
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.04)',
                            color: colors.textSecondary,
                            borderRadius: '10px',
                            width: 36,
                            height: 36,
                            '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent },
                            '&:disabled': { bgcolor: 'rgba(0,0,0,0.02)', color: colors.textTertiary },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Analytics Mode Toggle */}
                    {analyticsMode && (
                        <Box
                            onClick={() => setAnalyticsMode(false)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: { xs: 0.75, sm: 1.5 },
                                py: 0.75,
                                borderRadius: '20px',
                                cursor: 'pointer',
                                bgcolor: 'rgba(59,130,246,0.1)',
                                border: `1px solid ${colors.accent}`,
                                transition: 'all 0.2s ease',
                                '&:hover': { bgcolor: 'rgba(59,130,246,0.15)' },
                            }}
                        >
                            <PsychologyIcon sx={{ fontSize: 18, color: colors.accent }} />
                            <Typography sx={{ fontFamily, fontWeight: 500, color: colors.accent, fontSize: '0.8125rem', display: { xs: 'none', sm: 'block' } }}>
                                Analytics
                            </Typography>
                        </Box>
                    )}

                    {/* Image Generation Mode Toggle */}
                    {imageGenerationMode && (
                        <Box
                            onClick={() => {
                                setImageGenerationMode(false);
                                setAttachedImage(null);
                                setAttachedImagePreview(null);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: { xs: 0.75, sm: 1.5 },
                                py: 0.75,
                                borderRadius: '20px',
                                cursor: 'pointer',
                                bgcolor: 'rgba(236, 72, 153, 0.1)',
                                border: '1px solid #ec4899',
                                transition: 'all 0.2s ease',
                                '&:hover': { bgcolor: 'rgba(236, 72, 153, 0.15)' },
                            }}
                        >
                            <AutoFixHighIcon sx={{ fontSize: 18, color: '#ec4899' }} />
                            <Typography sx={{ fontFamily, fontWeight: 500, color: '#ec4899', fontSize: '0.8125rem', display: { xs: 'none', sm: 'block' } }}>
                                Image Generation
                            </Typography>
                        </Box>
                    )}

                    {/* Attached Image Preview */}
                    {imageGenerationMode && attachedImagePreview && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: 48,
                                height: 48,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid rgba(0,0,0,0.08)',
                            }}
                        >
                            <img
                                src={attachedImagePreview}
                                alt="Attached"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <IconButton
                                size="small"
                                onClick={removeAttachedImage}
                                sx={{
                                    position: 'absolute',
                                    top: -6,
                                    right: -6,
                                    bgcolor: colors.danger,
                                    color: '#fff',
                                    width: 18,
                                    height: 18,
                                    '&:hover': { bgcolor: '#b91c1c' },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Box>
                    )}

                    {/* Hidden file input for image attachment */}
                    <input
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        onChange={handleImageAttachment}
                        style={{ display: 'none' }}
                    />

                    {/* Textarea */}
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="How can I help you today?"
                        rows={1}
                        disabled={isSending}
                        style={{
                            flex: 1,
                            resize: 'none',
                            outline: 'none',
                            border: 'none',
                            background: 'transparent',
                            padding: '8px 8px',
                            fontSize: '0.875rem',
                            fontFamily,
                            maxHeight: 200,
                            minHeight: 24,
                            lineHeight: 1.5,
                            color: colors.textPrimary,
                        }}
                    />

                    {/* Character count */}
                    <Typography sx={{ fontFamily, color: colors.textTertiary, fontSize: '0.75rem', minWidth: 20, textAlign: 'center' }}>
                        {input.length > 0 ? input.length : ''}
                    </Typography>

                    {/* Send Button */}
                    <IconButton
                        type="submit"
                        disabled={isSending || !input.trim()}
                        sx={{
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            color: '#fff',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' },
                            '&:disabled': { bgcolor: 'rgba(0,0,0,0.06)', color: colors.textTertiary, boxShadow: 'none' },
                        }}
                    >
                        <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </form>

            {/* Quick Action Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, justifyContent: 'flex-start' }}>
                {quickActions
                    .filter((action) => {
                        if (action.id === 'analytics' && analyticsMode) return false;
                        if (action.id === 'image-generation' && imageGenerationMode) return false;
                        return true;
                    })
                    .map((action) => (
                        <Chip
                            key={action.id}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>{action.icon}</span>
                                    <span>{action.label}</span>
                                </Box>
                            }
                            onClick={() => handleQuickAction(action.id)}
                            disabled={isSending}
                            size="small"
                            sx={{
                                fontFamily,
                                bgcolor: 'rgba(255,255,255,0.8)',
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '10px',
                                px: 0.5,
                                backdropFilter: 'blur(8px)',
                                '&:hover': {
                                    bgcolor: 'rgba(59,130,246,0.06)',
                                    borderColor: 'rgba(59,130,246,0.2)',
                                },
                                '& .MuiChip-label': {
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '0.8125rem',
                                    fontWeight: 400,
                                },
                            }}
                        />
                    ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '14px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                }}
            >
                {/* Empty state */}
                {isEmptyChat && !isLoading ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: { xs: 1.5, sm: 2 }, py: { xs: 2, sm: 4 } }}>
                        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
                            <Box sx={{
                                width: { xs: 44, sm: 56 },
                                height: { xs: 44, sm: 56 },
                                borderRadius: { xs: '12px', sm: '16px' },
                                background: `linear-gradient(135deg, ${settings.primary_color || colors.accent}, ${settings.primary_color || colors.accent}dd)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: { xs: 2, sm: 3 },
                                boxShadow: `0 8px 24px ${settings.primary_color || colors.accent}4d`,
                            }}>
                                <AutoAwesomeIcon sx={{ fontSize: { xs: 22, sm: 28 }, color: settings.secondary_color || '#fff' }} />
                            </Box>
                            <Typography sx={{
                                fontFamily,
                                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                fontWeight: 700,
                                color: colors.textPrimary,
                                letterSpacing: '-0.02em',
                                mb: 1,
                            }}>
                                {settings.greeting_text || 'How can I help you today?'}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: colors.textTertiary, mb: { xs: 2, sm: 4 } }}>
                                Ask me anything, I'm here to help.
                            </Typography>
                            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                                {inputArea}
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {/* Messages */}
                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                bgcolor: 'rgba(248,250,252,0.5)',
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: 'rgba(0,0,0,0.08)',
                                    borderRadius: '4px',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.15)' },
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: 'rgba(0,0,0,0.08) transparent',
                            }}
                        >
                            <Box sx={{ width: { xs: '95%', sm: '90%' }, mx: 'auto', py: { xs: 1.5, sm: 3 } }}>
                                {messages.map((msg) => (
                                    <ChatSpaceMessageBubble
                                        key={msg.id}
                                        msg={msg}
                                        settings={settings}
                                        onCitations={() => handleCitationsClick(msg)}
                                        onCopy={handleCopy}
                                    />
                                ))}

                                {isSending && (
                                    <Box sx={{ display: 'flex', gap: 2, px: 2, py: 3 }}>
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: settings.primary_color,
                                                color: settings.secondary_color,
                                                borderRadius: '10px',
                                            }}
                                        >
                                            <SmartToyIcon sx={{ fontSize: 20 }} />
                                        </Avatar>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            {[0, 150, 300].map((delay) => (
                                                <Box
                                                    key={delay}
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: colors.textTertiary,
                                                        animation: 'bounce 1.4s infinite both',
                                                        animationDelay: `${delay}ms`,
                                                        '@keyframes bounce': {
                                                            '0%, 80%, 100%': { transform: 'scale(0)' },
                                                            '40%': { transform: 'scale(1)' },
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                <div ref={messagesEndRef} />
                            </Box>
                        </Box>

                        {/* Footer input */}
                        <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(255,255,255,0.6)', flexShrink: 0, px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 2 } }}>
                            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                                {inputArea}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    variant="filled"
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Add Menu */}
            <Menu
                anchorEl={addMenuAnchor}
                open={addMenuOpen}
                onClose={handleAddMenuClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.06)',
                    }
                }}
            >
                {imageGenerationMode ? (
                    <MenuItem
                        onClick={() => {
                            handleAddMenuClose();
                            imageInputRef.current?.click();
                        }}
                        sx={{ fontFamily, py: 1.25 }}
                    >
                        <ListItemIcon><ImageIcon fontSize="small" sx={{ color: colors.accent }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>Attach Image (Optional)</ListItemText>
                    </MenuItem>
                ) : (
                    <>
                        <MenuItem onClick={handleOpenWebsiteLinkDialog} sx={{ fontFamily, py: 1.25 }}>
                            <ListItemIcon><LinkIcon fontSize="small" sx={{ color: colors.accent }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>Add Website Link</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenDocumentDialog} sx={{ fontFamily, py: 1.25 }}>
                            <ListItemIcon><DescriptionIcon fontSize="small" sx={{ color: colors.accent }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>Upload Document</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenImageDialog} sx={{ fontFamily, py: 1.25 }}>
                            <ListItemIcon><ImageIcon fontSize="small" sx={{ color: colors.accent }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>Upload Image</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenAudioDialog} sx={{ fontFamily, py: 1.25 }}>
                            <ListItemIcon><MicIcon fontSize="small" sx={{ color: colors.accent }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>Upload Audio</ListItemText>
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Website Link Dialog */}
            <Dialog open={websiteLinkDialogOpen} onClose={handleCloseWebsiteLinkDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon sx={{ color: colors.accent }} />
                    Add Website Link
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, mb: 2 }}>
                        Enter a website URL to scrape and train your chatbot. The content will be automatically processed and added to your knowledge base.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Website Link"
                        variant="outlined"
                        value={websiteLink}
                        onChange={(e) => setWebsiteLink(e.target.value)}
                        placeholder="https://example.com"
                        disabled={isAddingWebsite}
                        sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                    />
                    {isAddingWebsite && <LinearProgress sx={{ mt: 2, borderRadius: '4px' }} />}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={handleCloseWebsiteLinkDialog} disabled={isAddingWebsite} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddWebsiteLink}
                        variant="contained"
                        disabled={isAddingWebsite || !websiteLink.trim()}
                        startIcon={isAddingWebsite ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                        sx={{
                            fontFamily,
                            textTransform: 'none',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' },
                        }}
                    >
                        {isAddingWebsite ? 'Adding...' : 'Add Link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Upload Dialog */}
            <Dialog open={documentDialogOpen} onClose={handleCloseDocumentDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon sx={{ color: colors.accent }} />
                    Upload Document
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, mb: 2 }}>
                        Upload document files (.pdf, .doc, .docx, .txt, .csv, .xls, .xlsx) to train your chatbot.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isUploadingDocument}
                            startIcon={<CloudUploadIcon />}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: colors.accent, color: colors.accent }}
                        >
                            Choose Files
                            <input type="file" multiple accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx" onChange={handleDocumentFileChange} hidden />
                        </Button>
                        {selectedDocuments && (
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
                                {selectedDocuments.length} file(s) selected
                            </Typography>
                        )}
                    </Box>
                    {isUploadingDocument && <LinearProgress sx={{ mt: 2, borderRadius: '4px' }} />}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={handleCloseDocumentDialog} disabled={isUploadingDocument} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>Cancel</Button>
                    <Button
                        onClick={handleUploadDocument}
                        variant="contained"
                        disabled={isUploadingDocument || !selectedDocuments}
                        startIcon={isUploadingDocument ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' } }}
                    >
                        {isUploadingDocument ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Upload Dialog */}
            <Dialog open={imageDialogOpen} onClose={handleCloseImageDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon sx={{ color: colors.accent }} />
                    Upload Image
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, mb: 2 }}>
                        Upload image files (.jpg, .jpeg, .png) to train your chatbot with visual content.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isUploadingImage}
                            startIcon={<ImageIcon />}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: colors.accent, color: colors.accent }}
                        >
                            Choose Images
                            <input type="file" multiple accept=".jpg,.jpeg,.png" onChange={handleImageFileChange} hidden />
                        </Button>
                        {selectedImages && (
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
                                {selectedImages.length} image(s) selected
                            </Typography>
                        )}
                    </Box>
                    {isUploadingImage && <LinearProgress sx={{ mt: 2, borderRadius: '4px' }} />}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={handleCloseImageDialog} disabled={isUploadingImage} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>Cancel</Button>
                    <Button
                        onClick={handleUploadImage}
                        variant="contained"
                        disabled={isUploadingImage || !selectedImages}
                        startIcon={isUploadingImage ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' } }}
                    >
                        {isUploadingImage ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Audio Recording Dialog */}
            <Dialog open={audioDialogOpen} onClose={handleCloseAudioDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MicIcon sx={{ color: colors.accent }} />
                    Record Audio
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, mb: 2 }}>
                        Record audio to transcribe and train your chatbot. Maximum recording time is 2 minutes.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={isRecording ? stopRecording : startRecording}
                            disabled={isUploadingAudio}
                            startIcon={isRecording ? <StopCircleIcon /> : <MicIcon />}
                            size="large"
                            sx={{
                                fontFamily,
                                textTransform: 'none',
                                borderRadius: '12px',
                                fontWeight: 600,
                                background: isRecording
                                    ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                                    : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                '&:hover': {
                                    background: isRecording
                                        ? 'linear-gradient(135deg, #b91c1c, #dc2626)'
                                        : 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                                },
                            }}
                        >
                            {isRecording ? 'Stop Recording' : 'Start Recording'}
                        </Button>
                        {isRecording && (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: colors.danger }}>
                                Recording: {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:
                                {(recordingTime % 60).toString().padStart(2, '0')}
                            </Typography>
                        )}
                        {isUploadingAudio && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} sx={{ color: colors.accent }} />
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Transcribing and uploading...</Typography>
                            </Box>
                        )}
                    </Box>
                    {isUploadingAudio && <LinearProgress sx={{ mt: 2, borderRadius: '4px' }} />}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={handleCloseAudioDialog} disabled={isRecording || isUploadingAudio} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
